import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Select,
  Skeleton,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@marrafa-gioale/gymx-ui-web'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ChevronDown, ScrollText, X } from 'lucide-react'
import { useState } from 'react'
import { z } from 'zod'

import { ApiError, errorMessage } from '@/api/errors'
import { useAuditEntries } from '@/api/queries'
import type { AuditEntry, AuditScope } from '@/api/types'
import { AUDIT_SCOPES } from '@/api/types'
import { PageHeader } from '@/components/layout/app-shell'
import { AuditDetailDialog } from '@/features/audit/audit-detail-dialog'
import {
  AUDIT_ACTION_LABELS,
  AUDIT_ACTION_PREFIX_LABELS,
  AUDIT_TARGET_LABELS,
  formatDateTime,
  formatRelative,
  labelForCode,
  plural,
} from '@/lib/format'
import { cn } from '@/lib/utils'

/**
 * Vista del registro — OPS-06.
 *
 * ## Perché non è una lista come le altre
 *
 * Le altre pagine di elenco usano `useCursorPagination` + `CursorPager`, che
 * mostrano «pagina N di M» e permettono di tornare indietro. Qui non si può:
 * `GET /audit` **non** restituisce `meta.total`, perché `audit_log` cresce senza
 * limite e contarlo filtrato per periodo sarebbe la query più lenta del
 * pannello. Senza totale non esiste un conteggio di pagine, e senza cursore
 * inverso non esiste un «indietro» che non sia ricaricare da capo.
 *
 * Resta «carica altro», che è anche il modo in cui un registro si legge davvero:
 * si parte dall'ultima cosa successa e si scende finché non si trova. Le pagine
 * già caricate restano a schermo, perché confrontare righe lontane fra loro è
 * metà del lavoro.
 *
 * ## I due ambiti
 *
 * Non sono una preferenza di visualizzazione ma due endpoint con due
 * autorizzazioni: la piattaforma vede tutto, l'organizzazione solo il proprio
 * tenant — quello che il selettore di TEN-06 governa. Sono in tab e non in un
 * select perché la differenza va vista, non cercata.
 */
const searchSchema = z.object({
  scope: z.enum(AUDIT_SCOPES).optional(),
  /** Prefisso dell'azione: `exercise`, `exercise.update`. */
  action: z.string().optional(),
  actorId: z.string().optional(),
  targetType: z.string().optional(),
  targetId: z.string().optional(),
  /** `YYYY-MM-DD` dai campi data; il server accetta ISO-8601. */
  from: z.string().optional(),
  to: z.string().optional(),
})

type AuditSearch = z.infer<typeof searchSchema>

export const Route = createFileRoute('/_app/registro/')({
  validateSearch: searchSchema,
  component: AuditPage,
})

const PAGE_SIZE = 50

const SCOPE_LABELS: Record<AuditScope, string> = {
  platform: 'Piattaforma',
  organization: 'Organizzazione',
}

/**
 * Estremi del periodo, da `YYYY-MM-DD` a ISO-8601.
 *
 * `to` diventa la **fine** della giornata: il backend confronta con `<=` su un
 * istante, quindi `2026-08-09` da solo escluderebbe tutto ciò che è successo
 * quel giorno — cioè quasi sempre proprio le righe che si stanno cercando.
 */
function toInstant(day: string | undefined, edge: 'start' | 'end'): string | undefined {
  if (!day) return undefined
  return edge === 'start' ? `${day}T00:00:00.000Z` : `${day}T23:59:59.999Z`
}

function AuditPage() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [opened, setOpened] = useState<AuditEntry | null>(null)

  const scope: AuditScope = search.scope ?? 'platform'

  const params = {
    scope,
    ...(search.action ? { action: search.action } : {}),
    ...(search.actorId ? { actorId: search.actorId } : {}),
    ...(search.targetType ? { targetType: search.targetType } : {}),
    ...(search.targetId ? { targetId: search.targetId } : {}),
    ...(toInstant(search.from, 'start') ? { from: toInstant(search.from, 'start') } : {}),
    ...(toInstant(search.to, 'end') ? { to: toInstant(search.to, 'end') } : {}),
    limit: PAGE_SIZE,
  }

  const list = useAuditEntries(params)

  const setFilter = <K extends keyof AuditSearch>(key: K, value: AuditSearch[K]) => {
    void navigate({
      search: (previous) => {
        const next = { ...previous }
        if (value === undefined || value === '') delete next[key]
        else next[key] = value
        return next
      },
    })
  }

  const hasFilters =
    Boolean(search.action) ||
    Boolean(search.actorId) ||
    Boolean(search.targetType) ||
    Boolean(search.targetId) ||
    Boolean(search.from) ||
    Boolean(search.to)

  // L'ambito non è un filtro: azzerare i filtri non deve rimandare l'operatore
  // sull'altro registro, che è una domanda diversa da quella che stava facendo.
  const clearFilters = () => void navigate({ search: { scope: search.scope } })

  const rows = list.data?.pages.flatMap((page) => page.data) ?? []

  return (
    <>
      <PageHeader
        title="Registro"
        description="Chi ha fatto cosa, e a chi. Sola lettura: un registro modificabile dall'API non è un registro."
      />

      <Tabs
        variant="pill"
        value={scope}
        onValueChange={(value) => setFilter('scope', value as AuditScope)}
        className="mb-4"
      >
        <TabsList>
          {AUDIT_SCOPES.map((item) => (
            <TabsTrigger key={item} value={item}>
              {SCOPE_LABELS[item]}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="mb-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Azione">
          {(props) => (
            <Select
              {...props}
              value={search.action ?? 'all'}
              onValueChange={(value) => setFilter('action', value === 'all' ? undefined : value)}
              items={[
                { value: 'all', label: 'Ogni azione' },
                ...Object.entries(AUDIT_ACTION_PREFIX_LABELS).map(([prefix, label]) => ({
                  value: prefix,
                  label,
                })),
              ]}
            />
          )}
        </Field>

        <Field label="Tipo di bersaglio">
          {(props) => (
            <Select
              {...props}
              value={search.targetType ?? 'all'}
              onValueChange={(value) =>
                setFilter('targetType', value === 'all' ? undefined : value)
              }
              items={[
                { value: 'all', label: 'Ogni bersaglio' },
                ...Object.entries(AUDIT_TARGET_LABELS).map(([key, label]) => ({
                  value: key,
                  label,
                })),
              ]}
            />
          )}
        </Field>

        <Field label="Dal giorno">
          {(props) => (
            <Input
              {...props}
              type="date"
              value={search.from ?? ''}
              onChange={(event) => setFilter('from', event.target.value || undefined)}
            />
          )}
        </Field>

        <Field label="Al giorno">
          {(props) => (
            <Input
              {...props}
              type="date"
              value={search.to ?? ''}
              onChange={(event) => setFilter('to', event.target.value || undefined)}
            />
          )}
        </Field>

        {/* `actorId` e `targetId` sono identificativi, non nomi: il backend non
            offre una ricerca per email, quindi l'unico modo di digitarli sarebbe
            copiarli. Si compilano quasi sempre da una riga — «tutte le azioni di
            questo attore» nel dettaglio — e restano scrivibili per chi arriva da
            una segnalazione con l'id già in mano. */}
        <Field
          label="Attore (id)"
          hint="Si compila anche dal dettaglio di una riga."
          className="lg:col-span-2"
        >
          {(props) => (
            <Input
              {...props}
              value={search.actorId ?? ''}
              onChange={(event) => setFilter('actorId', event.target.value || undefined)}
              placeholder="00000000-0000-4000-8000-000000000000"
              className="font-mono text-xs"
            />
          )}
        </Field>

        <Field label="Bersaglio (id)" className="lg:col-span-2">
          {(props) => (
            <Input
              {...props}
              value={search.targetId ?? ''}
              onChange={(event) => setFilter('targetId', event.target.value || undefined)}
              placeholder="seed_bench_press_0"
              className="font-mono text-xs"
            />
          )}
        </Field>
      </div>

      {hasFilters ? (
        <div className="mb-3">
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <X />
            Azzera filtri
          </Button>
        </div>
      ) : null}

      {list.isError && !list.data ? (
        <ErrorState
          message={errorMessage(list.error)}
          requestId={list.error instanceof ApiError ? list.error.requestId : undefined}
          onRetry={() => void list.refetch()}
        />
      ) : list.isPending ? (
        <ListSkeleton />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<ScrollText />}
          title={hasFilters ? 'Nessuna riga con questi filtri' : 'Registro vuoto'}
          description={
            hasFilters
              ? 'Prova ad allargare il periodo o a togliere un filtro.'
              : scope === 'organization'
                ? 'In questa organizzazione non è ancora successo niente di registrabile.'
                : 'Nessuna azione amministrativa registrata.'
          }
        />
      ) : (
        <>
          <div className={cn('transition-opacity', list.isFetching && 'opacity-60')}>
            <ul className="flex flex-col gap-2">
              {rows.map((entry) => (
                <AuditRow key={entry.id} entry={entry} onOpen={() => setOpened(entry)} />
              ))}
            </ul>
          </div>

          <div className="flex flex-col items-center gap-2 pt-4">
            {list.hasNextPage ? (
              <Button
                variant="outline"
                loading={list.isFetchingNextPage}
                onClick={() => void list.fetchNextPage()}
              >
                <ChevronDown />
                Carica altre righe
              </Button>
            ) : (
              <p className="text-muted-foreground text-xs">Fine del registro per questi filtri.</p>
            )}
            <p className="text-muted-foreground text-xs" data-numeric>
              {plural(rows.length, 'riga caricata', 'righe caricate')}
            </p>
          </div>
        </>
      )}

      {/* Nessun `meta.total`: il conteggio delle righe caricate è tutto ciò che si
          può dire onestamente, e dirlo è meglio che lasciare intendere un totale. */}
      {scope === 'organization' ? (
        <Alert variant="neutral" className="mt-4" title="Che cosa mostra questo elenco">
          Solo le azioni dell'organizzazione attiva. Import del catalogo e moderazione degli account
          non compaiono qui: non avvengono dentro una palestra e si guardano dal registro di
          piattaforma.
        </Alert>
      ) : null}

      <AuditDetailDialog
        scope={scope}
        entry={opened}
        open={opened !== null}
        onOpenChange={(open) => !open && setOpened(null)}
        onFilterActor={(actorId) => setFilter('actorId', actorId)}
      />
    </>
  )
}

/**
 * Una riga del registro.
 *
 * Non è una tabella nemmeno su desktop: le colonne utili cambiano con l'azione —
 * un `exercise.update` ha un bersaglio con id lungo, un `admin.login` non ha
 * bersaglio affatto — e una tabella riempirebbe metà delle celle di trattini.
 */
function AuditRow({ entry, onOpen }: { entry: AuditEntry; onOpen: () => void }) {
  return (
    <li>
      <Card radius="md" padding="none" className="hover:bg-surface-alt/60">
        <button type="button" onClick={onOpen} className="w-full p-3 text-left">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">
              {labelForCode(entry.action, AUDIT_ACTION_LABELS)}
            </span>
            <code className="text-muted-foreground font-mono text-xs">{entry.action}</code>
            {entry.organizationId === null ? <Badge variant="secondary">Piattaforma</Badge> : null}
          </div>

          <p className="text-muted-foreground mt-1 text-xs">
            {entry.actorEmail ?? 'attore sconosciuto'}
            {entry.targetType ? (
              <>
                {' → '}
                {labelForCode(entry.targetType, AUDIT_TARGET_LABELS)}{' '}
                <code className="font-mono break-all">{entry.targetId ?? '—'}</code>
              </>
            ) : null}
          </p>

          <p className="text-muted-foreground mt-1 text-xs">
            <time dateTime={entry.createdAt} title={formatDateTime(entry.createdAt)}>
              {formatRelative(entry.createdAt)}
            </time>
            {entry.ip ? ` · ${entry.ip}` : null}
          </p>
        </button>
      </Card>
    </li>
  )
}

function ListSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 10 }, (_, index) => (
        <div key={index} className="border-border bg-card space-y-2 rounded-md border p-3">
          <Skeleton className="h-4 w-56" />
          <Skeleton className="h-3 w-72" />
          <Skeleton className="h-3 w-32" />
        </div>
      ))}
    </div>
  )
}
